/**
 * Language Validator
 *
 * Uses highlight.js to detect programming language from code snippets
 * and validate against user's selected language.
 *
 * Tree-shaken imports to keep bundle size minimal (~85KB for 13 languages)
 */

import hljs from 'highlight.js/lib/core'

// Import only the languages we support in SyntaxLens
import javascript from 'highlight.js/lib/languages/javascript'
import typescript from 'highlight.js/lib/languages/typescript'
import python from 'highlight.js/lib/languages/python'
import rust from 'highlight.js/lib/languages/rust'
import go from 'highlight.js/lib/languages/go'
import kotlin from 'highlight.js/lib/languages/kotlin'
import swift from 'highlight.js/lib/languages/swift'
import java from 'highlight.js/lib/languages/java'
import csharp from 'highlight.js/lib/languages/csharp'
import cpp from 'highlight.js/lib/languages/cpp'
import c from 'highlight.js/lib/languages/c'
import sql from 'highlight.js/lib/languages/sql'
import julia from 'highlight.js/lib/languages/julia'

// Register languages with highlight.js
hljs.registerLanguage('javascript', javascript)
hljs.registerLanguage('typescript', typescript)
hljs.registerLanguage('python', python)
hljs.registerLanguage('rust', rust)
hljs.registerLanguage('go', go)
hljs.registerLanguage('kotlin', kotlin)
hljs.registerLanguage('swift', swift)
hljs.registerLanguage('java', java)
hljs.registerLanguage('csharp', csharp)
hljs.registerLanguage('cpp', cpp)  // Use 'cpp' not 'c++'
hljs.registerLanguage('c', c)
hljs.registerLanguage('sql', sql)
hljs.registerLanguage('julia', julia)

// No language families - each language is distinct
// C and C++ are separate, JavaScript and TypeScript are separate

/**
 * Normalize language names from highlight.js to our display names
 */
const languageNameMap: Record<string, string> = {
  'javascript': 'JavaScript',
  'typescript': 'TypeScript',
  'python': 'Python',
  'rust': 'Rust',
  'go': 'Go',
  'kotlin': 'Kotlin',
  'swift': 'Swift',
  'java': 'Java',
  'csharp': 'C#',
  'cpp': 'C++',
  'c': 'C',
  'sql': 'SQL',
  'julia': 'Julia'
}

export interface ValidationResult {
  isMismatch: boolean
  detected: string
  rawRelevance: number
}

/**
 * Validate if detected language exactly matches selected language
 *
 * Simple exact match - no thresholds, no fuzzy logic
 * C and C++ are different, JavaScript and TypeScript are different
 *
 * @param code - The code snippet to analyze
 * @param selectedLanguage - The language the user selected (e.g., "Python", "JavaScript")
 * @returns Validation result with mismatch status and detected language
 */
export function validateCodeLanguage(
  code: string,
  selectedLanguage: string
): ValidationResult {
  // Skip validation for empty or very short code
  if (!code || code.trim().length < 10) {
    return {
      isMismatch: false,
      detected: selectedLanguage,
      rawRelevance: 0
    }
  }

  try {
    // Use highlight.js auto-detection with our registered languages only
    const registeredLanguages = ['javascript', 'typescript', 'python', 'rust', 'go', 'kotlin', 'swift', 'java', 'csharp', 'cpp', 'c', 'sql', 'julia']
    const result = hljs.highlightAuto(code.trim(), registeredLanguages)

    // Get detected language and normalize the name
    const detectedLang = result.language || 'unknown'
    const detectedDisplayName = languageNameMap[detectedLang] || detectedLang

    // Exact match check (case-insensitive)
    const isMismatch = detectedDisplayName.toLowerCase() !== selectedLanguage.toLowerCase()

    // Debug logging
    console.log('🔍 Language Detection (Exact Match):', {
      selectedLanguage,
      detectedLanguage: detectedDisplayName,
      rawRelevance: result.relevance,
      isMismatch,
      rawDetected: result.language
    })

    return {
      isMismatch,
      detected: detectedDisplayName,
      rawRelevance: Math.round(result.relevance)
    }
  } catch (error) {
    console.error('Language detection error:', error)

    // On error, assume no mismatch to avoid blocking user
    return {
      isMismatch: false,
      detected: selectedLanguage,
      rawRelevance: 0
    }
  }
}

/**
 * Get confidence level category for UI display
 */
export function getConfidenceLevel(confidence: number): 'high' | 'medium' | 'low' {
  if (confidence >= 90) return 'high'
  if (confidence >= 70) return 'medium'
  return 'low'
}

/**
 * Get color class for confidence display
 */
export function getConfidenceColor(confidence: number): string {
  if (confidence >= 90) return 'text-green-600'
  if (confidence >= 70) return 'text-orange-600'
  return 'text-red-600'
}
